/* eslint-disable @typescript-eslint/no-unused-vars */
import JsPDF from 'jspdf'
import moment from 'moment'
import React from 'react'

import LiveTooltip from 'live/components/ui/tooltip'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { exportFormatist } from '../../../../../utils/constants'
import {
    exportSystemAuditLogsCSV,
    exportSystemAuditLogsJson,
    exportSystemAuditLogsPDF,
    getSystemAuditLogs
} from '../../../../services/systemAudit'
import { ConfigHeaderExportContainer, ConfigHeaderExportDropdown, ExportFormatList } from './styles'

interface IProps {
    startDay?: string | null
    endDay?: string | null
    startHour?: string | null
    endHour?: string | null
    quickSearch: string
    filterAuthor: ISelect
    filterStatus: ISelect
    filterHttpVerb: ISelect
    setSystemAuditDataToPDF: (k: ISystemAudit[]) => void

    // setIsLoading: (k: boolean) => void
}

export const ConfigHeaderExport: React.FC<IProps> = ({
    startDay,
    startHour,
    endDay,
    endHour,
    filterAuthor,
    filterHttpVerb,
    filterStatus,
    quickSearch,
    setSystemAuditDataToPDF

    // setIsLoading
}) => {
    const [show, setShow] = React.useState<boolean>(false)
    const dropdownRef = React.useRef<HTMLDivElement>(null)
    const [isDownloading, setIsDownloading] = React.useState<boolean>(false)

    const dateFormatToRequisition = (dateToConvert): string => {
        const initialDateToRequisition = new Date(dateToConvert)
        const formatedInitialDate = moment(initialDateToRequisition).format('YYYY-MM-DD')

        return formatedInitialDate
    }

    const generatePDF = (): void => {
        const report = new JsPDF('landscape', 'pt', 'a4')

        try {
            report.html(document.querySelector('#pdf-table-container').outerHTML).then(() => {
                report.save('report.pdf')
            })
        } catch (e) {
            console.log(e)
        } finally {
            setIsDownloading(false)
            setSystemAuditDataToPDF([])
        }
    }

    const handleExportPDF = async (): Promise<void> => {
        try {
            setIsDownloading(true)

            const data = await getSystemAuditLogs({
                startDay: dateFormatToRequisition(startDay),
                startHour: startHour,
                endDay: dateFormatToRequisition(endDay),
                endHour: endHour,
                perPage: 300,
                quickSearch: quickSearch,
                filterByStatusCodeStatus: filterStatus.value,
                filterByHTTPVerb: filterHttpVerb.value,
                filterByLiveAuthor: filterAuthor.value
            })
            setSystemAuditDataToPDF(data.data)
            generatePDF()
        } catch (error) {
            globalNotification.error(i18n('An error occurred on listing audit logs'))
            console.log(error)
        }
    }

    const handleExportSystemAuditDataJson = async (): Promise<void> => {
        if (startDay && startHour && endDay && endHour) {
            try {
                setIsDownloading(true)
                const data = await exportSystemAuditLogsJson({
                    startDay: dateFormatToRequisition(startDay),
                    startHour: startHour,
                    endDay: dateFormatToRequisition(endDay),
                    endHour: endHour,
                    filterByStatusCodeStatus: filterStatus.value,
                    filterByHTTPVerb: filterHttpVerb.value,
                    filterByLiveAuthor: filterAuthor.value,
                    quickSearch: quickSearch
                })
            } catch (error) {
                globalNotification.error(i18n('An error occurred on export data'))
            } finally {
                setIsDownloading(false)
            }
        } else {
            globalNotification.error(i18n('Please, enter a time range'))
        }
    }

    const handleExportSystemAuditDataCsv = async (): Promise<void> => {
        if (startDay && startHour && endDay && endHour) {
            try {
                setIsDownloading(true)
                const data = await exportSystemAuditLogsCSV({
                    startDay: dateFormatToRequisition(startDay),
                    startHour: startHour,
                    endDay: dateFormatToRequisition(endDay),
                    endHour: endHour,
                    filterByStatusCodeStatus: filterStatus.value,
                    filterByHTTPVerb: filterHttpVerb.value,
                    filterByLiveAuthor: filterAuthor.value,
                    quickSearch: quickSearch
                })
            } catch (error) {
                globalNotification.error(i18n('An error occurred on export data'))
            } finally {
                setIsDownloading(false)
            }
        } else {
            globalNotification.error(i18n('Please, enter a time range'))
        }
    }

    React.useEffect(() => {
        function handleMouseDownAction(this: Document, ev: MouseEvent): void {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(ev.target as HTMLElement) &&
                !(ev.target as HTMLElement).className.includes('Select-option')
            ) {
                setShow(false)
            }
        }

        document.addEventListener('mousedown', handleMouseDownAction)

        return () => {
            document.removeEventListener('mousedown', handleMouseDownAction)
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <ConfigHeaderExportContainer ref={dropdownRef}>
            <div
                className="configheaderexport-clickable"
                id="systemAudit-configHeaderExport-clickable"
                onClick={() => {
                    setShow(old => !old)
                }}
            >
                <i className="fas fa-arrow-alt-down" />
                <div className="label">{i18n('EXPORT')}</div>
                <i className={`${show ? 'fas fa-caret-up' : 'fas fa-caret-down'}`} />
            </div>
            <ConfigHeaderExportDropdown style={{ visibility: show ? 'visible' : 'hidden' }}>
                <div className="dropdown-tile">
                    <span>{i18n('DOWNLOAD DATA')}</span>
                </div>
                <ExportFormatList>
                    <li>
                        <div className="listitem-wrapper">
                            <span>JSON</span>
                        </div>
                        <div className="listitem-wrapper">
                            <button
                                className={`download-span ${isDownloading ? 'downloading' : ''}`}
                                onClick={() => handleExportSystemAuditDataCsv()}
                                id="systemAudit-configHeaderExport-downloadJson-button"
                                disabled={isDownloading}
                            >
                                {isDownloading ? <i className="fa fa-spinner fa-spin" /> : i18n('Download')}
                            </button>
                        </div>
                    </li>
                    <li>
                        <div className="listitem-wrapper">
                            <span>CSV</span>
                        </div>
                        <div className="listitem-wrapper">
                            <button
                                className={`download-span ${isDownloading ? 'downloading' : ''}`}
                                onClick={() => handleExportSystemAuditDataCsv()}
                                id="systemAudit-configHeaderExport-downloadCSV-button"
                                disabled={isDownloading}
                            >
                                {isDownloading ? <i className="fa fa-spinner fa-spin" /> : i18n('Download')}
                            </button>
                        </div>
                    </li>
                    <li>
                        <div className="listitem-wrapper">
                            <span>PDF</span>
                        </div>
                        <div className="listitem-wrapper">
                            <LiveTooltip
                                content={i18n('PDF export only shows the latest 300 requisitions')}
                                position="bottom"
                                hide={!show}
                            >
                                <button
                                    disabled={isDownloading}
                                    className="download-span"
                                    onClick={() => handleExportPDF()}
                                >
                                    {isDownloading ? <i className="fa fa-spinner fa-spin" /> : i18n('Download')}
                                </button>
                            </LiveTooltip>
                        </div>
                    </li>
                </ExportFormatList>
            </ConfigHeaderExportDropdown>
        </ConfigHeaderExportContainer>
    )
}
